import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from "@mui/material";
import { db } from "../../firebase"; // Adjust path as needed
import { collection, getDocs, query, where, Timestamp, onSnapshot } from "firebase/firestore";

function MonthlySummaryReport() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [payModes, setPayModes] = useState([]);
  
  // State for the Details Modal (Used for Expenses, Pigmi/Loans, and Other Income)
  const [selectedDetailCategory, setSelectedDetailCategory] = useState(null);

  // Fetch payment modes dynamically for name matching
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "payment_modes"), (snap) => {
      const modes = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPayModes(modes);
    });
    return () => unsub();
  }, []);

  // Aggregated state
  const [reportData, setReportData] = useState({
    saleCash: 0,
    saleOnlineBank: 0,
    salePending: 0,
    totalSale: 0,

    currentSalesByMode: {},
    oldDuesByMode: {},
    totalReceived: 0,

    expenses: [],
    operatingExpenseSubtotal: 0,

    oldDuesCustomers: [],
    totalOldDues: 0,

    otherIncomes: [],
    transferLikeTotal: 0,

    purchases: [],
    purchaseSubtotal: 0,

    pigmiLoanPayments: 0,
    pigmiLoanDetails: [],
    totalPigmiLoan: 0,

    reportedOperatingCost: 0,
    currentSaleReceived: 0,
    provisionalOperatingProfit: 0,
    cashBeforePowerPayment: 0,
  });

  const fetchReportData = async () => {
    if (!fromDate || !toDate) return alert("Select date range");
    setLoading(true);

    try {
      const startObj = new Date(`${fromDate}T00:00:00`);
      const endObj = new Date(`${toDate}T23:59:59`);
      
      const start = Timestamp.fromDate(startObj);
      const end = Timestamp.fromDate(endObj);

      // Queries
      const qTrips = query(collection(db, "trips"), where("grossDate", ">=", start), where("grossDate", "<=", end));
      const qPay = query(collection(db, "customer_history"), where("date", ">=", start), where("date", "<=", end));
      const qExpenses = query(collection(db, "otherExpenses"), where("date", ">=", start), where("date", "<=", end));
      const qOtherIncome = query(collection(db, "otherIncome"), where("date", ">=", start), where("date", "<=", end));
      const qPurchases = query(collection(db, "purchases"), where("purchasedate", ">=", fromDate), where("purchasedate", "<=", toDate));
      const qPigmi = query(collection(db, "pigmi_savings"), where("depositDate", ">=", fromDate), where("depositDate", "<=", toDate));
      
      // Fetching all required collections including incomeHeads and otherIncome
      const [
        tripSnap, paySnap, expenseSnap, otherIncomeSnap, purchaseSnap, pigmiSnap, 
        customersSnap, expHeadsSnap, incomeHeadsSnap, dealersSnap, productsSnap
      ] = await Promise.all([
        getDocs(qTrips), getDocs(qPay), getDocs(qExpenses), getDocs(qOtherIncome), getDocs(qPurchases), getDocs(qPigmi), 
        getDocs(collection(db, "customers")), getDocs(collection(db, "expenseHeads")), 
        getDocs(collection(db, "incomeHeads")), getDocs(collection(db, "dealers")), getDocs(collection(db, "purchase_products"))
      ]);

      const customerMap = {};
      customersSnap.forEach(doc => { customerMap[doc.id] = doc.data().customerName || doc.data().name || "Unknown"; });

      const headMap = {};
      expHeadsSnap.forEach(doc => { headMap[doc.id] = doc.data().name; });

      const incomeHeadMap = {};
      incomeHeadsSnap.forEach(doc => { incomeHeadMap[doc.id] = doc.data().name; });

      const productMap = {};
      productsSnap.forEach(doc => { productMap[doc.id] = doc.data().prodname || doc.data().productName || "Unknown Product"; });

      const getNormalizedMode = (rawMode) => {
        if (!rawMode) return "Cash";
        const found = payModes.find(m => m.payMode?.toLowerCase() === rawMode.toLowerCase());
        return found ? found.payMode : rawMode;
      };

      // --- Process Sales & Current Collections ---
      let salePending = 0; 
      let totalSale = 0;
      let currentSalesByMode = {};

      tripSnap.docs.forEach(doc => {
        const t = doc.data();
        const tripTotal = Number(t.totalAmount || t.calculatedTotal || 0);
        totalSale += tripTotal;
        salePending += Number(t.remainingAmount || 0);

        (t.payments || []).forEach(p => {
          if (p.date || p.paymentDate || p.createdAt) {
            const rawDate = p.date || p.paymentDate || p.createdAt;
            let pDateObj = rawDate.toDate ? rawDate.toDate() : new Date(rawDate);
            if (pDateObj && (pDateObj < startObj || pDateObj > endObj)) return; 
          }

          const amt = Number(p.amount || 0);
          const mode = getNormalizedMode(p.mode || p.paymentMode);
          currentSalesByMode[mode] = (currentSalesByMode[mode] || 0) + amt;
        });
      });

      let saleCash = 0;
      let saleOnlineBank = 0;
      Object.entries(currentSalesByMode).forEach(([mode, amt]) => {
        if (mode.toLowerCase().includes("cash")) { saleCash += amt; } else { saleOnlineBank += amt; }
      });
      const currentSaleReceived = saleCash + saleOnlineBank;

      // --- Process Old Dues Collections ---
      let oldDuesByMode = {};
      let oldDuesCustomersMap = {};
      let totalOldDues = 0;
      
      paySnap.docs.forEach(doc => {
        const p = doc.data();
        if (!p.tripId) { 
          const amt = Number(p.amount || p.paidAmount || 0);
          const mode = getNormalizedMode(p.payMode || p.paymentMode);
          const custName = p.customerName || customerMap[p.customerId] || "Other customers";

          oldDuesCustomersMap[custName] = (oldDuesCustomersMap[custName] || 0) + amt;
          oldDuesByMode[mode] = (oldDuesByMode[mode] || 0) + amt;
          totalOldDues += amt;
        }
      });

      const sortedCustomers = Object.entries(oldDuesCustomersMap).sort((a, b) => b[1] - a[1]);
      const oldDuesCustomers = sortedCustomers.slice(0, 5).map(c => ({ name: c[0], amount: c[1] }));
      const otherCustAmt = sortedCustomers.slice(5).reduce((sum, c) => sum + c[1], 0);
      if (otherCustAmt > 0) oldDuesCustomers.push({ name: "Other customers", amount: otherCustAmt });

      const totalReceived = currentSaleReceived + totalOldDues;

      // --- Process Expenses (Grouped by Head with Details) ---
      let expensesMap = {};
      let operatingExpenseSubtotal = 0;
      expenseSnap.docs.forEach(doc => {
        const e = doc.data();
        const amt = Number(e.amount || 0);
        
        const headId = e.expenseHeadId || "uncategorized";
        const headName = headMap[headId] || e.category || e.expenseHead || "Other Expenses";

        if (!expensesMap[headId]) {
          expensesMap[headId] = { name: headName, amount: 0, details: [] };
        }

        expensesMap[headId].amount += amt;
        expensesMap[headId].details.push({
          id: doc.id,
          date: e.date?.toDate() || null,
          description: e.description || "-",
          amount: amt
        });

        operatingExpenseSubtotal += amt;
      });
      const expenses = Object.values(expensesMap);

      // --- Process Other Income (Grouped by Income Head with Details) ---
      let otherIncomeMap = {};
      let transferLikeTotal = 0;
      otherIncomeSnap.docs.forEach(doc => {
        const inc = doc.data();
        const amt = Number(inc.amount || 0);

        const headId = inc.incomeHeadId || "uncategorized";
        const headName = incomeHeadMap[headId] || inc.category || "Other Income";

        if (!otherIncomeMap[headId]) {
          otherIncomeMap[headId] = { name: headName, amount: 0, details: [] };
        }

        otherIncomeMap[headId].amount += amt;
        otherIncomeMap[headId].details.push({
          id: doc.id,
          date: inc.date?.toDate() || null,
          description: inc.description || "-",
          amount: amt
        });

        transferLikeTotal += amt;
      });
      const otherIncomes = Object.values(otherIncomeMap);

      // --- Process Purchases (Product-Wise Grouping) ---
      let purchasesMap = {};
      let purchaseSubtotal = 0;
      purchaseSnap.docs.forEach(doc => {
        const p = doc.data();
        
        if (p.items && Array.isArray(p.items) && p.items.length > 0) {
          p.items.forEach(item => {
            const prodName = productMap[item.productId] || "Unknown Product";
            const itemTotal = Number(item.finaltotal || item.total || (Number(item.qty || 0) * Number(item.rate || 0)) || 0);
            
            if (!purchasesMap[prodName]) { purchasesMap[prodName] = 0; }
            purchasesMap[prodName] += itemTotal;
            purchaseSubtotal += itemTotal;
          });
        } else {
          const amt = Number(p.grandtotal || p.calculatedTotal || 0);
          if (amt > 0) {
            const category = p.category || "Uncategorized Purchase";
            if (!purchasesMap[category]) { purchasesMap[category] = 0; }
            purchasesMap[category] += amt;
            purchaseSubtotal += amt;
          }
        }
      });
      const purchases = Object.entries(purchasesMap).map(([name, amount]) => ({ name, amount }));

      // --- Process Pigmi/Loans ---
      let pigmiLoanPayments = 0;
      let pigmiLoanDetails = [];
      pigmiSnap.docs.forEach(doc => {
        const p = doc.data();
        const amt = Number(p.amount || 0);
        pigmiLoanPayments += amt;
        
        let parsedDate = null;
        if (p.depositDate) {
          parsedDate = new Date(p.depositDate);
        }

        pigmiLoanDetails.push({
          id: doc.id,
          date: parsedDate,
          description: p.description || p.narration || p.customerName || "Pigmi / Loan Deposit",
          amount: amt
        });
      });

      const reportedOperatingCost = operatingExpenseSubtotal + purchaseSubtotal;
      const provisionalOperatingProfit = totalSale - reportedOperatingCost;
      const cashBeforePowerPayment = totalReceived - reportedOperatingCost;

      setReportData({
        saleCash, saleOnlineBank, salePending, totalSale,
        currentSalesByMode, oldDuesByMode, totalReceived,
        expenses, operatingExpenseSubtotal,
        oldDuesCustomers, totalOldDues,
        otherIncomes, transferLikeTotal,
        purchases, purchaseSubtotal,
        pigmiLoanPayments,
        pigmiLoanDetails,
        totalPigmiLoan: pigmiLoanPayments,
        reportedOperatingCost, currentSaleReceived, provisionalOperatingProfit, cashBeforePowerPayment
      });

      setDataLoaded(true);
    } catch (err) {
      console.error("Fetch Error:", err);
      alert("Error fetching data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatAmt = (amt) => "₹" + new Intl.NumberFormat('en-IN').format(Math.round(amt || 0));
  
  const getHeaderDate = () => {
    if (!toDate) return "";
    const d = new Date(toDate);
    const month = d.toLocaleString('default', { month: 'short' }).toUpperCase();
    const year = d.getFullYear().toString().slice(-2);
    return `${month}-${year}`;
  };

  const colors = {
    darkBlue: "#1e2a38",
    lightBlueBg: "#eef2ff",
    lightGreenBg: "#ecfdf5",
    lightRedBg: "#fff1f2",
    borderColor: "#c7d2fe"
  };

  const tableStyle = { width: '100%', borderCollapse: 'collapse', marginBottom: '24px', border: `1px solid ${colors.borderColor}` };
  const thStyle = { backgroundColor: colors.darkBlue, color: '#fff', padding: '8px 12px', fontSize: '0.85rem', textAlign: 'left', fontWeight: 'bold' };
  const tdStyle = { borderBottom: `1px solid ${colors.borderColor}`, padding: '6px 12px', fontSize: '0.8rem', color: '#1f2937' };
  const tdAmountStyle = { ...tdStyle, textAlign: 'right', fontWeight: 'bold' };

  return (
    <Box p={2} sx={{ bgcolor: "#f1f5f9", minHeight: "100vh" }}>
      {/* ================= PRINT STYLE CSS ================= */}
      <style>{`
        @media print {
            body * {
                visibility: hidden;
            }
            .printable-area, .printable-area * {
                visibility: visible;
            }
            .printable-area {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
            }
            .no-print {
                display: none !important;
            }
            @page {
                size: A4 landscape;
                margin: 10mm;
            }
        }
      `}</style>

      {/* Inputs */}
      <Card className="no-print" sx={{ p: 1, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="date" label="From Date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="date" label="To Date" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="contained" onClick={fetchReportData} disabled={loading} sx={{ mr: 1, bgcolor: colors.darkBlue }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : "Generate"}
            </Button>
            <Button variant="outlined" onClick={() => window.print()} disabled={!dataLoaded}>Print</Button>
          </Grid>
        </Grid>
      </Card>

      {/* Main Report Document */}
      {dataLoaded && (
        <Box className="printable-area" p={2}>
          <Box sx={{ border: "2px solid #000", p: 4, bgcolor: '#fff', borderRadius: 2 }}>
            
            {/* Main Header */}
            <Box sx={{ bgcolor: colors.darkBlue, color: 'white', p: 3, borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" sx={{ letterSpacing: 1 }}>Monthly Sales, Collection & Expense Summary</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ letterSpacing: 1 }}>{getHeaderDate()}</Typography>
            </Box>

            <Grid className="row" spacing={4}>
              {/* LEFT COLUMN */}
              <Grid className="col-sm-6 mb-2">
                
                {/* SALE SUMMARY */}
                <table style={tableStyle}>
                  <thead><tr><th colSpan={2} style={{...thStyle, borderRadius: '4px 4px 0 0'}}>SALE SUMMARY</th></tr></thead>
                  <tbody>
                    <tr><td style={tdStyle}>Sale for Cash</td><td style={tdAmountStyle}>{formatAmt(reportData.saleCash)}</td></tr>
                    <tr><td style={tdStyle}>Sale for Online / Bank</td><td style={tdAmountStyle}>{formatAmt(reportData.saleOnlineBank)}</td></tr>
                    <tr><td style={tdStyle}>Sale for A/C (Pending)</td><td style={tdAmountStyle}>{formatAmt(reportData.salePending)}</td></tr>
                    <tr style={{ backgroundColor: colors.lightBlueBg }}>
                      <td style={{...tdStyle, fontWeight: 'bold'}}>TOTAL SALE</td>
                      <td style={tdAmountStyle}>{formatAmt(reportData.totalSale)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* REPORTED OPERATING EXPENSES */}
                <table style={tableStyle}>
                  <thead><tr><th colSpan={2} style={{...thStyle, borderRadius: '4px 4px 0 0'}}>REPORTED OPERATING EXPENSES</th></tr></thead>
                  <tbody>
                    {reportData.expenses.map((exp, i) => (
                      <tr 
                        key={`exp-${i}`} 
                        onClick={() => setSelectedDetailCategory(exp)}
                        style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="Click to view details"
                      >
                        <td style={{...tdStyle, color: '#2563eb', textDecoration: 'underline'}}>{exp.name}</td>
                        <td style={tdAmountStyle}>{formatAmt(exp.amount)}</td>
                      </tr>
                    ))}
                    {reportData.expenses.length === 0 && <tr><td colSpan={2} style={{...tdStyle, textAlign:'center'}}>No data</td></tr>}
                    <tr style={{ backgroundColor: colors.lightRedBg }}>
                      <td style={{...tdStyle, fontWeight: 'bold'}}>OPERATING EXPENSE SUBTOTAL</td>
                      <td style={tdAmountStyle}>{formatAmt(reportData.operatingExpenseSubtotal)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* PURCHASES */}
                <table style={tableStyle}>
                  <thead><tr><th colSpan={2} style={{...thStyle, borderRadius: '4px 4px 0 0'}}>PURCHASES</th></tr></thead>
                  <tbody>
                    {reportData.purchases.map((pur, i) => (
                      <tr key={`pur-${i}`}>
                        <td style={tdStyle}>{pur.name}</td>
                        <td style={tdAmountStyle}>{formatAmt(pur.amount)}</td>
                      </tr>
                    ))}
                     {reportData.purchases.length === 0 && <tr><td colSpan={2} style={{...tdStyle, textAlign:'center'}}>No data</td></tr>}
                    <tr style={{ backgroundColor: colors.lightRedBg }}>
                      <td style={{...tdStyle, fontWeight: 'bold'}}>PURCHASE SUBTOTAL</td>
                      <td style={tdAmountStyle}>{formatAmt(reportData.purchaseSubtotal)}</td>
                    </tr>
                  </tbody>
                </table>

              </Grid>

              {/* RIGHT COLUMN */}
              <Grid className="col-sm-6 mb-2">
                
                {/* PAYMENT MODE / COLLECTION */}
                <table style={tableStyle}>
                  <thead><tr><th colSpan={2} style={{...thStyle, borderRadius: '4px 4px 0 0'}}>PAYMENT MODE / COLLECTION</th></tr></thead>
                  <tbody>
                    {Object.entries(reportData.currentSalesByMode).map(([mode, amt], i) => (
                      <tr key={`curr-${i}`}>
                        <td style={tdStyle}>Current sales - {mode}</td>
                        <td style={tdAmountStyle}>{formatAmt(amt)}</td>
                      </tr>
                    ))}
                    {Object.entries(reportData.oldDuesByMode).map(([mode, amt], i) => (
                      <tr key={`old-${i}`}>
                        <td style={tdStyle}>Old dues - {mode}</td>
                        <td style={tdAmountStyle}>{formatAmt(amt)}</td>
                      </tr>
                    ))}
                    {Object.keys(reportData.currentSalesByMode).length === 0 && Object.keys(reportData.oldDuesByMode).length === 0 && (
                      <tr><td colSpan={2} style={{...tdStyle, textAlign:'center'}}>No payment data</td></tr>
                    )}
                    <tr style={{ backgroundColor: colors.lightGreenBg }}>
                      <td style={{...tdStyle, fontWeight: 'bold'}}>TOTAL RECEIVED</td>
                      <td style={tdAmountStyle}>{formatAmt(reportData.totalReceived)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* OLD DUES COLLECTION - CUSTOMER */}
                <table style={tableStyle}>
                  <thead><tr><th colSpan={2} style={{...thStyle, borderRadius: '4px 4px 0 0'}}>OLD DUES COLLECTION - CUSTOMER</th></tr></thead>
                  <tbody>
                    {reportData.oldDuesCustomers.map((cust, i) => (
                      <tr key={i}><td style={tdStyle}>{cust.name}</td><td style={tdAmountStyle}>{formatAmt(cust.amount)}</td></tr>
                    ))}
                    {reportData.oldDuesCustomers.length === 0 && <tr><td colSpan={2} style={{...tdStyle, textAlign:'center'}}>No data</td></tr>}
                    <tr style={{ backgroundColor: colors.lightGreenBg }}>
                      <td style={{...tdStyle, fontWeight: 'bold'}}>TOTAL OLD DUES</td>
                      <td style={tdAmountStyle}>{formatAmt(reportData.totalOldDues)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* BANK / OWNER / TSPL / OTHER INCOME ENTRIES */}
                <table style={tableStyle}>
                  <thead><tr><th colSpan={2} style={{...thStyle, borderRadius: '4px 4px 0 0'}}>BANK / OWNER / TSPL ENTRIES</th></tr></thead>
                  <tbody>
                    {reportData.otherIncomes.map((inc, i) => (
                      <tr 
                        key={`inc-${i}`}
                        onClick={() => setSelectedDetailCategory(inc)}
                        style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="Click to view details"
                      >
                        <td style={{...tdStyle, color: '#2563eb', textDecoration: 'underline'}}>{inc.name}</td>
                        <td style={tdAmountStyle}>{formatAmt(inc.amount)}</td>
                      </tr>
                    ))}
                    {reportData.otherIncomes.length === 0 && <tr><td colSpan={2} style={{...tdStyle, textAlign:'center', color: '#9ca3af'}}>—</td></tr>}
                    <tr style={{ backgroundColor: colors.lightBlueBg }}>
                      <td style={{...tdStyle, fontWeight: 'bold'}}>TRANSFER-LIKE TOTAL</td>
                      <td style={tdAmountStyle}>{formatAmt(reportData.transferLikeTotal)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* PIGMI / LOAN PAYMENTS */}
                <table style={tableStyle}>
                  <thead><tr><th colSpan={2} style={{...thStyle, borderRadius: '4px 4px 0 0'}}>PIGMI / LOAN PAYMENTS</th></tr></thead>
                  <tbody>
                    <tr 
                      onClick={() => setSelectedDetailCategory({ name: "Pigmi / Loan Payments", details: reportData.pigmiLoanDetails })}
                      style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Click to view details"
                    >
                      <td style={{...tdStyle, color: '#2563eb', textDecoration: 'underline'}}>Pigmi + Loan Payments</td>
                      <td style={tdAmountStyle}>{formatAmt(reportData.pigmiLoanPayments)}</td>
                    </tr>
                    <tr style={{ backgroundColor: colors.lightBlueBg }}>
                      <td style={{...tdStyle, fontWeight: 'bold'}}>TOTAL PIGMI / LOAN</td>
                      <td style={tdAmountStyle}>{formatAmt(reportData.totalPigmiLoan)}</td>
                    </tr>
                  </tbody>
                </table>

              </Grid>
            </Grid>

            {/* MANAGEMENT SUMMARY WIDGET */}
            <Box sx={{ border: `1px solid ${colors.borderColor}`, borderRadius: '12px', p: 3, mt: 2, bgcolor: '#f8fafc' }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: colors.darkBlue, mb: 3 }}>MANAGEMENT SUMMARY</Typography>
              <Grid container spacing={4}>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>Total Sale</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f172a' }}>{formatAmt(reportData.totalSale)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>Current Sale Received</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#16a34a' }}>{formatAmt(reportData.currentSaleReceived)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>Current Sale Pending</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#dc2626' }}>{formatAmt(reportData.salePending)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>Reported Operating Cost (A)</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#dc2626' }}>{formatAmt(reportData.reportedOperatingCost)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>Provisional Operating Profit</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#2563eb' }}>{formatAmt(reportData.provisionalOperatingProfit)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold' }}>Cash Before Power Payment</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#ea580c' }}>{formatAmt(reportData.cashBeforePowerPayment)}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      )}

      {/* DETAILS MODAL (Used for displaying Expense, Other Income, and Pigmi/Loan specifics on click) */}
      <Dialog 
        open={Boolean(selectedDetailCategory)} 
        onClose={() => setSelectedDetailCategory(null)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: colors.darkBlue, color: 'white', fontWeight: 'bold' }}>
          Details for: {selectedDetailCategory?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 0, mt: 2 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description / Info</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedDetailCategory?.details.map((detail) => (
                <TableRow key={detail.id} hover>
                  <TableCell>
                    {detail.date ? detail.date.toLocaleDateString('en-GB') : '-'}
                  </TableCell>
                  <TableCell>{detail.description}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: '500' }}>
                    {formatAmt(detail.amount)}
                  </TableCell>
                </TableRow>
              ))}
              {selectedDetailCategory?.details.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">No details available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button 
            onClick={() => setSelectedDetailCategory(null)} 
            variant="contained" 
            sx={{ bgcolor: colors.darkBlue }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default MonthlySummaryReport;